import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import ListViewSettings from "../../components/ListViewSettings";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { buildListExcelExportHeader } from "../../utils/listExcelExportHeader";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllDesignMix, deleteDesignMix, printDesignMix } from "../../services/DesignMixServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildDesignMixQueryParams } from "../../utils/listViewDateRanges";
import { openPdfResponse } from "../../utils/openPdfInApp";
import moment from 'moment';

function DesignMixTable() {
    const InitialData = {
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const [listViewConfig, setListViewConfig] = useState(null);
    const left_pinned = ["work_schedule_no", "sales_order_no"];
    const right_pinned = ["id"];

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const summaryTotals = useMemo(() => {
        const todayRecords = data.records.filter(
            (row) => row.work_schedule_date === todayDateLabel
        );
        return {
            total_count: data.records.length,
            today_count: todayRecords.length,
            scheduled_qty: data.records.reduce((sum, row) => sum + (Number(row.scheduled_quantity) || 0), 0),
            order_qty: data.records.reduce((sum, row) => sum + (Number(row.ordered_quantity) || 0), 0),
        };
    }, [data.records, todayDateLabel]);

    const todayMixCard = useMemo(() => ({
        label: 'Today Mix',
        value: String(summaryTotals.today_count),
        gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 55%, #fbbf24 100%)',
        shadow: '0 8px 20px rgba(245, 158, 11, 0.28)',
    }), [summaryTotals.today_count]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Total Mix',
            value: String(summaryTotals.total_count),
            gradient: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #60a5fa 100%)',
            shadow: '0 8px 20px rgba(37, 99, 235, 0.24)',
        },
        {
            label: 'Sch Qty',
            value: Number(summaryTotals.scheduled_qty || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 55%, #a78bfa 100%)',
            shadow: '0 8px 20px rgba(124, 58, 237, 0.24)',
        },
        {
            label: 'Order Qty',
            value: Number(summaryTotals.order_qty || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #2dd4bf 100%)',
            shadow: '0 8px 20px rgba(13, 148, 136, 0.24)',
        },
    ]), [summaryTotals]);

    const columns = useMemo(
        () => [
            { fieldName: 'plant', headerName: 'Plant', size: 65 },
            { fieldName: 'sales_order_no', headerName: 'SO No', size: 88 },
            { fieldName: 'sales_order_date', headerName: 'SO Date', size: 86 },
            { fieldName: 'work_schedule_no', headerName: 'WS No', size: 72 },
            { fieldName: 'work_schedule_date', headerName: 'WS Date', size: 86 },
            { fieldName: 'customer_id', headerName: 'Customer', size: 120 },
            { fieldName: 'consignee_id', headerName: 'Site/Project', size: 120 },
            { fieldName: 'grade', headerName: 'Grade', size: 75 },
            { fieldName: 'rate', headerName: 'Rate', size: 68, align: 'right' },
            { fieldName: 'ordered_quantity', headerName: 'Ord Qty', size: 72, align: 'right' },
            { fieldName: 'scheduled_quantity', headerName: 'Sch Qty', size: 72, align: 'right' },
            { fieldName: 'delivery_mode', headerName: 'Delivery', size: 82 },
            { fieldName: 'concrete_structure_id', headerName: 'Structure', size: 95 },
            { fieldName: 'id', headerName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const onEditDesignMix = (designmixId) => {
        navigate(`/DesignMixEdit/${designmixId}/`);
    };

    const onPrintDesignMix = (designmixId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printDesignMix(cookies, designmixId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Design Mix Report',
                    downloadFileName: `design_mix_${designmixId}.pdf`,
                });
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Print Failed');
            });
    };

    const onDeleteDesignMix = (designmixId, event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Are you sure to Delete?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                deleteDesignMix(cookies, designmixId)
                    .then(() => {
                        Swal.fire('Deleted Successfully!', '', 'success');
                        setRefreshKey((oldKey) => oldKey + 1);
                    })
                    .catch((error) => {
                        setIsLoading(false);
                        displayError(error.response?.data ?? error.message, 'Delete Failed');
                    });
            } else if (result.isDismissed) {
                Swal.fire('Not Deleted', '', 'info');
            }
        });
    };

    const handleCSVExport = async () => {
        const documentTitle = 'List of Design Mix';
        const exportHeader = await buildListExcelExportHeader(cookies, listViewConfig?.plant_ids, {
            documentTitle,
        });
        const infoLines = [...exportHeader.infoLines];
        if (!exportHeader.plantAlias && data.records[0]?.plant) {
            infoLines.unshift(`Plant: ${data.records[0].plant}`);
        }
        await exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
            data.records.map((r) => ({
                plant: r.plant,
                sales_order_no: r.sales_order_no,
                sales_order_date: r.sales_order_date,
                work_schedule_no: r.work_schedule_no,
                work_schedule_date: r.work_schedule_date,
                customer_id: r.customer_id,
                consignee_id: r.consignee_id,
                grade: r.grade,
                rate: r.rate,
                ordered_quantity: r.ordered_quantity,
                scheduled_quantity: r.scheduled_quantity,
                delivery_mode: r.delivery_mode,
                concrete_structure_id: r.concrete_structure_id,
            })),
            'design_mix_list',
            {
                documentTitle,
                infoLines,
                header: {
                    companyName: exportHeader.companyName,
                    addressLines: exportHeader.addressLines,
                    gstNo: exportHeader.gstNo,
                    logoDataUrl: exportHeader.logoDataUrl,
                },
                totalKeys: ['ordered_quantity', 'scheduled_quantity', 'rate'],
            },
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Design Mix',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((r) => ({
                plant: r.plant,
                sales_order_no: r.sales_order_no,
                sales_order_date: r.sales_order_date,
                work_schedule_no: r.work_schedule_no,
                work_schedule_date: r.work_schedule_date,
                customer_id: r.customer_id,
                consignee_id: r.consignee_id,
                grade: r.grade,
                rate: r.rate,
                ordered_quantity: r.ordered_quantity,
                scheduled_quantity: r.scheduled_quantity,
                delivery_mode: r.delivery_mode,
                concrete_structure_id: r.concrete_structure_id,
            })),
            'DesignMixList.pdf',
        );
    };

    const loadDesignMixList = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllDesignMix(cookies, buildDesignMixQueryParams(config))
            .then((designmixList) => {
                if (!designmixList?.design_mix_list) {
                    throw new Error('Design mix list was not returned by the server.');
                }
                const tableData = designmixList.design_mix_list.map((r) => {
                    const salesOrder = r.work_schedule?.sales_order_detail?.sales_order;
                    const prefix = salesOrder?.prefix ?? '';
                    const orderNo = salesOrder?.order_no ?? '';
                    return {
                        id: r.id,
                        sales_order_no: `${prefix}${orderNo}`,
                        sales_order_date: salesOrder?.order_date ?? '',
                        work_schedule_no: r.work_schedule?.schedule_no ?? '',
                        work_schedule_date: r.work_schedule?.schedule_date ?? '',
                        scheduled_quantity: r.work_schedule?.scheduled_qty ?? 0,
                        customer_id: salesOrder?.consignee?.customer?.name ?? '',
                        consignee_id: salesOrder?.consignee?.project_name ?? salesOrder?.consignee?.name ?? '',
                        grade: r.work_schedule?.sales_order_detail?.product?.name ?? '',
                        rate: r.work_schedule?.sales_order_detail?.rate ?? 0,
                        ordered_quantity: r.work_schedule?.sales_order_detail?.quantity ?? 0,
                        delivery_mode: (r.work_schedule?.sales_order_detail?.delivery_mode ?? r.work_schedule?.delivery_mode ?? '').toUpperCase(),
                        concrete_structure_id: r.work_schedule?.sales_order_detail?.concrete_structure?.name ?? '',
                        plant: r.work_schedule?.plant?.alias_name ?? '',
                    };
                });

                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Failed to load design mix list');
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        setListViewConfig(config);
        loadDesignMixList(config);
    }, [loadDesignMixList]);

    useEffect(() => {
        getListViewSettings(cookies, 'design_mix')
            .then((response) => {
                const config = response.config || {};
                setListViewConfig(config);
                loadDesignMixList(config);
            })
            .catch((error) => {
                displayError(error.response?.data ?? error.message, 'Failed to load list settings');
            });
    }, [cookies, loadDesignMixList, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/DesignMix"
                    addNewLabel="New Design Mix"
                    homeLink="/Home"
                />
                <div className="container">
                    <PremiumPageHeader
                        module="QUALITY CONTROL"
                        title="List of Design Mix"
                        subtitle="Configure filters in General Settings. The table loads based on your saved preferences."
                        modeLabel="VIEW MODE"
                        headerActions={
                            <ListViewSettings
                                viewKey="design_mix"
                                onSaved={handleSettingsSaved}
                            />
                        }
                    />
                </div>

                <Box
                    className="container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        mb: 2.5,
                        py: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            flexShrink: 0,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: todayMixCard.gradient,
                                boxShadow: todayMixCard.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                {todayMixCard.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                {todayMixCard.value}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 1.25,
                            flex: 1,
                            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                            minWidth: 0,
                            overflowX: 'auto',
                        }}
                    >
                        {summaryCards.map((card) => (
                            <Box
                                key={card.label}
                                sx={{
                                    flex: '0 0 auto',
                                    minWidth: 128,
                                    borderRadius: '12px',
                                    background: card.gradient,
                                    boxShadow: card.shadow,
                                    px: 1.75,
                                    py: 1.1,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                    {card.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF DESIGN MIX"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditDesignMix}
                            onRowDelete={onDeleteDesignMix}
                            onRowPrint={onPrintDesignMix}
                            hidePrint={false}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete={true}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            compactRows={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DesignMixTable;
